import { useTheme } from '@crackpoint/shared/components';
import type { HintWithMetadata } from '@crackpoint/shared/types';
import { strings } from '../../config';
import { cn } from '@crackpoint/shared/utils';

interface HintCardProps {
  hint: HintWithMetadata;
  index?: number;
  isNew?: boolean;
  className?: string;
  onClick?: () => void;
}

export function HintCard({ hint, index, isNew = false, className, onClick }: HintCardProps) {
  const { theme } = useTheme();

  const isPowerUp = hint.power_up !== null;
  const rarity = hint.rarity;

  const getRarityColour = (): string => {
    if (isPowerUp && hint.power_up) {
      return theme.colours.powerUp[hint.power_up];
    }
    if (rarity) {
      return theme.colours.rarity[rarity];
    }
    return theme.colours.text.secondary;
  };

  const getGlowStyle = (): string => {
    const colour = getRarityColour();
    return `0 0 ${isNew ? '30px' : '20px'} ${colour}40`;
  };

  const getBorderColour = (): string => {
    return getRarityColour();
  };

  const getFeedbackDescription = (): string => {
    if (hint.correct_placed === 0 && hint.correct_misplaced === 0) {
      return strings.hints.nothingCorrect;
    }

    if (hint.correct_placed > 0 && hint.correct_misplaced > 0) {
      return strings.hints.placedAndMisplaced(hint.correct_placed, hint.correct_misplaced);
    }

    if (hint.correct_placed > 0) {
      return strings.hints.digitsPlaced(hint.correct_placed);
    }

    return strings.hints.digitsMisplaced(hint.correct_misplaced);
  };

  return (
    <div
      className={cn('hint-card', isNew && 'hint-card--new', onClick && 'hint-card--clickable', className)}
      onClick={onClick}
      style={{
        position: 'relative',
        backgroundColor: theme.colours.background.secondary,
        border: `2px solid ${getBorderColour()}`,
        borderRadius: theme.borders.radius.md,
        boxShadow: isNew ? getGlowStyle() : theme.shadows.md,
        padding: theme.spacing.md,
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${theme.transitions.normal}`,
        overflow: 'hidden',
      }}
    >
      {isNew && (
        <div
          className="hint-card__new-indicator"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: getRarityColour(),
            color: theme.colours.text.inverse,
            fontSize: theme.typography.fontSize.xs,
            fontWeight: theme.typography.fontWeight.bold,
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            borderRadius: `0 ${theme.borders.radius.sm} 0 ${theme.borders.radius.sm}`,
          }}
        >
          {strings.hints.newBadge}
        </div>
      )}

      <div
        className="hint-card__header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.sm,
        }}
      >
        <div className="hint-card__digits" style={{ display: 'flex', gap: theme.spacing.xs }}>
          {hint.digits.map((digit, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2rem',
                height: '2rem',
                backgroundColor: theme.colours.background.tertiary,
                color: theme.colours.text.primary,
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.bold,
                borderRadius: theme.borders.radius.sm,
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              {digit}
            </span>
          ))}
        </div>

        {index !== undefined && (
          <span
            className="hint-card__index"
            style={{
              fontSize: theme.typography.fontSize.sm,
              color: theme.colours.text.tertiary,
              fontWeight: theme.typography.fontWeight.medium,
            }}
          >
            #{index + 1}
          </span>
        )}
      </div>

      <div
        className="hint-card__feedback"
        style={{
          color: theme.colours.text.primary,
          fontSize: theme.typography.fontSize.md,
          lineHeight: theme.typography.lineHeight.normal,
        }}
      >
        {getFeedbackDescription()}
      </div>

      <div
        className="hint-card__meta"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: theme.spacing.sm,
          paddingTop: theme.spacing.sm,
          borderTop: `1px solid ${theme.colours.background.tertiary}`,
        }}
      >
        {isPowerUp && hint.power_up ? (
          <div
            className="hint-card__power-up"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              color: theme.colours.powerUp[hint.power_up],
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
            }}
          >
            <span style={{ fontSize: theme.typography.fontSize.xl }}>
              {strings.hints.powerUpIcons[hint.power_up]}
            </span>
            <span>{strings.hints.powerUps[hint.power_up]}</span>
          </div>
        ) : rarity ? (
          <span
            className="hint-card__rarity"
            style={{
              color: theme.colours.rarity[rarity],
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
              textTransform: 'uppercase',
            }}
          >
            {strings.hints.rarity[rarity]}
          </span>
        ) : null}

        {hint.percentile > 0 && (
          <span
            className="hint-card__percentile"
            style={{
              fontSize: theme.typography.fontSize.xs,
              color: theme.colours.text.tertiary,
            }}
          >
            {strings.hints.percentile(Math.round(hint.percentile * 100))}
          </span>
        )}
      </div>
    </div>
  );
}
